/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-transactions
 * @spec docs/superpowers/specs/2026-10-13-wallet-network-awareness-design.md §4.3
 * Network switching utility hook
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ConnectedWallet } from '@privy-io/react-auth';
import { useOptionalWalletContext } from '@/hooks/useWalletContext';
import { useLiveChainId } from '@/hooks/useLiveChainId';
import {
  getWalletKind,
  isLikelyLedger,
  describeManualSwitch,
  type WalletKind,
} from '@/lib/wallet-kind';
import { getSwitchableChain } from '@/lib/chain-registry';
import { parseChainIdHex } from '@/lib/network-gate';
import { classifySigningError, unsupportedChainMessage } from '@/lib/signing-errors';
import { WalletActionError } from '@/lib/wallet-actions/errors';
import { showToast, TOAST_MESSAGES } from '@/lib/toast';

const CANCELLED_MESSAGE = 'Network switch was cancelled';

/**
 * How long a wallet gets to report the new chain after accepting a switch. MetaMask (and Privy's
 * wrapper around injected wallets) resolve wallet_switchEthereumChain before eth_chainId reports
 * the new chain, so a single immediate read showed "did not complete" for a switch that had
 * worked. The read is retried until it matches or this window ends.
 */
export const SWITCH_SETTLE_MS = 2_000;
const SWITCH_POLL_MS = 100;

/** Read eth_chainId until it reports `chainId` or the settle window ends; the last read wins. */
async function readChainAfterSwitch(
  provider: { request: (args: { method: string }) => Promise<unknown> },
  chainId: number
): Promise<number | null> {
  const deadline = Date.now() + SWITCH_SETTLE_MS;
  for (;;) {
    const read = parseChainIdHex(await provider.request({ method: 'eth_chainId' }));
    if (read === chainId || Date.now() >= deadline) return read;
    await new Promise((resolve) => setTimeout(resolve, SWITCH_POLL_MS));
  }
}
const PENDING_MESSAGE = 'Check your wallet: a request is already open.';

function isSameWallet(a: ConnectedWallet | null, b: ConnectedWallet | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return !!a.address && !!b.address && a.address.toLowerCase() === b.address.toLowerCase();
}

export interface UseNetworkSwitchReturn {
  /** The wallet this hook reads and switches — the SIGNING wallet, never an arbitrary wallets[0] */
  wallet: ConnectedWallet | null;
  /** How that wallet reaches the dapp; drives the manual-switch guidance */
  walletKind: WalletKind;
  /** Best-effort Ledger detection (WalletConnect peer name / client type only) */
  isLedger: boolean;
  /** Current chain ID */
  currentChainId: number | null;
  /** Whether the current chain matches the target */
  isCorrectNetwork: (targetChainId: number) => boolean;
  /** Switch to a different network */
  switchNetwork: (chainId: number) => Promise<boolean>;
  /** Whether a switch is in progress */
  isSwitching: boolean;
  /** Error message if switch failed */
  error: string | null;
  /**
   * What the user can do by hand when the programmatic switch failed or silently no-op'd.
   * Kept separate from `error` so existing error strings stay stable; null when not applicable
   * (no failure yet, or the user cancelled).
   */
  switchHint: string | null;
}

/**
 * Hook for managing network switching
 *
 * Used to ensure users are on the correct network before
 * executing transactions (Base for staking, Polygon for USDT claims)
 *
 * Reads the WalletProvider context optionally: outside a provider there is no selected wallet,
 * so the default target is "no wallet" instead of a throw.
 *
 * @param targetWallet - Optional specific wallet to check/switch. Omit to use the selected
 *   (signing) wallet; pass null for "no wallet".
 */
export function useNetworkSwitch(targetWallet?: ConnectedWallet | null): UseNetworkSwitchReturn {
  // Optional: leaf UI using this hook also renders in component tests without a WalletProvider.
  const walletContext = useOptionalWalletContext();
  const selectedWallet = walletContext?.selectedWallet ?? null;
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switchHint, setSwitchHint] = useState<string | null>(null);

  // Resolve like useSigningWallet: an explicit argument (including null) wins; otherwise the
  // SELECTED wallet. Never `wallets[0]` — with an embedded wallet plus an external EOA connected,
  // wallets[0] is usually the embedded wallet (always on Base), so the switch UI reported
  // "correct network" while useStakingSignature signed with the external wallet on another chain
  // (2026-09-03 Ledger incident: "Wrong network" on submit after a successful-looking switch).
  const wallet = targetWallet !== undefined ? targetWallet : selectedWallet;
  const walletKind = getWalletKind(wallet);
  const isLedger = isLikelyLedger(wallet);

  // Chain source. The selected wallet's chain is the app-wide subscription in WalletProvider. Any
  // other target (AddressPage walletOverride) gets its own; passing null keeps hook order stable.
  const readsContext = walletContext !== null && isSameWallet(wallet, selectedWallet);
  const local = useLiveChainId(readsContext ? null : wallet);
  const currentChainId = readsContext ? (walletContext?.chainId ?? null) : local.chainId;
  const refreshChainId =
    readsContext && walletContext ? walletContext.refreshChainId : local.refresh;

  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  const walletAddress = wallet?.address;

  // The failure state belongs to the wallet that failed. Carrying it across a wallet change would
  // leave a stale Ledger hint (and its "Disconnect & reconnect" action) pointing at the NEW wallet,
  // which reconnect would then disconnect (6673f090). Its own effect since the chain subscription
  // moved to useLiveChainId.
  useEffect(() => {
    setError(null);
    setSwitchHint(null);
  }, [walletAddress]);

  const isCorrectNetwork = useCallback(
    (targetChainId: number): boolean => {
      return currentChainId === targetChainId;
    },
    [currentChainId]
  );

  const switchNetwork = useCallback(
    async (chainId: number): Promise<boolean> => {
      if (!wallet) {
        setError('No wallet connected');
        return false;
      }

      const chain = getSwitchableChain(chainId);
      if (!chain) {
        setError('Unsupported network');
        return false;
      }

      // Already on the correct network. WalletContext reads the live chain for every wallet kind,
      // the embedded wallet included (spec §7.5), so the reported chain is trusted.
      if (currentChainId === chainId) {
        return true;
      }

      setIsSwitching(true);
      setError(null);
      setSwitchHint(null);

      // The request belongs to THIS wallet. If the selected wallet changes while the request is
      // pending, the late completion must not write state (liveChainId/error/toasts) — the new
      // wallet would otherwise appear to be on the target chain without ever switching.
      const requestAddress = wallet.address;
      const isStale = () => walletRef.current?.address !== requestAddress;

      try {
        const provider = await wallet.getEthereumProvider();

        const chainHex = `0x${chainId.toString(16)}`;
        try {
          await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainHex }],
          });
        } catch (switchError: unknown) {
          // The wallet does not know the chain: a 4902 (also nested inside a wrapper, F7) or its
          // own "unsupported/unrecognized chain" wording. Offer it, then ask for the switch again.
          if (classifySigningError(switchError).category !== 'unsupported-chain') {
            throw switchError;
          }
          try {
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: chainHex,
                  chainName: chain.name,
                  nativeCurrency: chain.nativeCurrency,
                  rpcUrls: [chain.rpcUrl],
                  blockExplorerUrls: [chain.explorerUrl],
                },
              ],
            });
          } catch (addError: unknown) {
            if (classifySigningError(addError).category === 'user_rejected') throw addError;
            // A 4902 after a failed add: only a fresh connection that approves the chain helps.
            throw new WalletActionError({
              message: unsupportedChainMessage(chainId),
              phase: 'prepare',
              category: 'unsupported-chain',
              walletKind,
              requiredChainId: chainId,
              cause: addError,
            });
          }
          // EIP-3085 lets a wallet ADD the chain without SELECTING it. Ask for the switch
          // again now that the chain exists; wallets that auto-selected on add treat this as
          // a no-op, and the verification below is the arbiter either way.
          await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainHex }],
          });
        }

        // Verify instead of trusting the resolved request: re-read the chain from the SAME
        // provider. A wallet may resolve wallet_addEthereumChain without selecting the chain, and
        // some providers no-op a switch silently — optimistically setting liveChainId would clear
        // the "Switch to Base" CTA while the wallet is still on the old chain, and the stake
        // submit-time chain check would then fail with the very error the CTA exists to prevent.
        // A malformed reply parses to null, so it can never count as verified.
        // Wallets report the new chain a moment after accepting the switch: keep reading briefly.
        const verifiedChainId = await readChainAfterSwitch(provider, chainId);

        // isSwitching always clears (it's this hook's single in-flight flag), but the rest of the
        // completion is dropped when stale — the switch happened on a wallet that is no longer
        // selected, so its chain must not leak into the new wallet's liveChainId.
        setIsSwitching(false);
        // Stale is a failure for the caller: the wallet it is about to sign with was never
        // switched. Reporting the old wallet's verified chain would wave the caller's own gate
        // through (StakeModal would proceed to sign on the wrong chain).
        if (isStale()) return false;

        if (verifiedChainId !== chainId) {
          const message =
            'Your wallet did not complete the network switch. Please switch networks inside your wallet.';
          setError(message);
          const hint = describeManualSwitch(walletKind, chain.name, isLedger);
          setSwitchHint(hint);
          showToast.error(TOAST_MESSAGES.NETWORK_SWITCH_FAILED, `${message} ${hint}`);
          return false;
        }

        showToast.success(TOAST_MESSAGES.NETWORK_SWITCH_SUCCESS, `Switched to ${chain.name}`);
        // Re-read through the active chain source: a provider that does not emit chainChanged would
        // otherwise keep the pre-switch chain and the gate.
        // Already behind isStale(): a completion for a wallet that is no longer the target never
        // reaches this line, so it cannot refresh the new wallet's chain.
        await refreshChainId();
        return true;
      } catch (err) {
        setIsSwitching(false);
        if (isStale()) return false;

        const classified = classifySigningError(err, { requiredChainId: chainId });
        const cancelled = classified.category === 'user_rejected';
        const pending = classified.category === 'wallet_unavailable';
        const unsupported = classified.category === 'unsupported-chain';
        const errorMessage = cancelled
          ? CANCELLED_MESSAGE
          : pending
            ? PENDING_MESSAGE
            : unsupported
              ? classified.message
              : err instanceof Error
                ? err.message
                : 'Failed to switch network';
        // An unsupported chain is fixed by reconnecting, never by switching (spec §7.3): only the
        // WalletConnect guidance says that, so it is the one hint kept for it.
        const hint =
          cancelled || pending
            ? null
            : unsupported
              ? walletKind === 'wallet_connect'
                ? describeManualSwitch(walletKind, chain.name, isLedger)
                : null
              : describeManualSwitch(walletKind, chain.name, isLedger);
        setError(errorMessage);
        setSwitchHint(hint);
        showToast.error(
          TOAST_MESSAGES.NETWORK_SWITCH_FAILED,
          hint ? `${errorMessage} ${hint}` : errorMessage
        );
        return false;
      }
    },
    [wallet, currentChainId, walletKind, isLedger, refreshChainId]
  );

  return {
    wallet,
    walletKind,
    isLedger,
    currentChainId,
    isCorrectNetwork,
    switchNetwork,
    isSwitching,
    error,
    switchHint,
  };
}

/**
 * Hook to ensure correct network before executing a callback
 */
export function useRequireNetwork(requiredChainId: number, targetWallet?: ConnectedWallet | null) {
  const { isCorrectNetwork, switchNetwork, isSwitching, currentChainId } =
    useNetworkSwitch(targetWallet);

  const ensureNetwork = useCallback(async (): Promise<boolean> => {
    if (isCorrectNetwork(requiredChainId)) {
      return true;
    }
    return switchNetwork(requiredChainId);
  }, [isCorrectNetwork, requiredChainId, switchNetwork]);

  return {
    isCorrectNetwork: isCorrectNetwork(requiredChainId),
    ensureNetwork,
    isSwitching,
    currentChainId,
    requiredChainId,
  };
}
